/**
 * Helpers for BK design scan `source_files` from get_bk_design_files_meta.
 */
import {
  CHECKPOINT_STEPS,
  LEGACY_SLOT_FALLBACK,
  checkpointSlotForStep,
} from '../constants/stepCheckpointSlots';

/**
 * True when a URL is a presigned R2 URL on the **private** R2 endpoint
 * (`*.r2.cloudflarestorage.com`). Such URLs are method-locked (GET vs PUT),
 * and using a PUT-signed URL with GET fails with `SignatureDoesNotMatch`.
 *
 * Frontend cannot reliably know which method was signed, so we treat these
 * as untrusted for GET reads and prefer truly public URLs instead.
 */
function isPrivateR2SignedUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const u = new URL(url);
    if (!/\.r2\.cloudflarestorage\.com$/i.test(u.hostname)) return false;
    return (
      u.searchParams.has('X-Amz-Signature') ||
      u.searchParams.has('X-Amz-Algorithm')
    );
  } catch {
    return false;
  }
}

/**
 * Pick first non-empty candidate URL that is safe for HTTP GET.
 * Skips presigned URLs on the private R2 endpoint (could be PUT-signed).
 */
function pickSafeGetUrl(...candidates) {
  for (const c of candidates) {
    if (typeof c !== 'string' || c.trim() === '') continue;
    if (isPrivateR2SignedUrl(c)) continue;
    return c;
  }
  return '';
}

export function getSourceEntryUrl(entry) {
  if (!entry) return '';
  // Prefer truly public URLs (e.g. `pub-*.r2.dev` or backend-served paths).
  // Skip private R2 presigned URLs which may be signed for PUT (upload) and
  // would 403 with `SignatureDoesNotMatch` when fetched with GET.
  return pickSafeGetUrl(
    entry.r2_url,
    entry.r2Url,
    entry.file_url,
    entry.presigned_url,
    entry.url,
  );
}

/**
 * Resolve download URL for a checkpoint entry from `get_bk_design_files_meta` → `checkpoints.*`.
 * Same resolution order as source files (see integration guide §5.1).
 */
export function getCheckpointMeshUrl(entry) {
  if (!entry) return '';
  return pickSafeGetUrl(
    entry.r2_url,
    entry.r2Url,
    entry.file_url,
    entry.presigned_url,
    entry.url,
  );
}

/**
 * BK locking checkpoint shape (see backend contract):
 *   { checkpoints: { locking: { plain: {...}|null, bulldog: {...}|null } } }
 *
 * Helpers below tolerate older flat shapes by treating `locking` as the entry
 * if neither `plain` nor `bulldog` keys are present.
 */
function unwrapLocking(meta) {
  return meta?.checkpoints?.locking ?? null;
}

export function getLockingPlainEntry(meta) {
  const locking = unwrapLocking(meta);
  if (!locking) return null;
  if ('plain' in locking || 'bulldog' in locking) return locking.plain ?? null;
  return locking; // legacy flat shape
}

/**
 * True when rectification checkpoint exists (server meta from `get_bk_design_files_meta`).
 * Prefers `bk_checkpoints.rectification.exists`; falls back to `checkpoints.rectification` shape.
 * @param {Record<string, unknown> | null | undefined} meta
 */
export function rectificationCheckpointExistsFromMeta(meta) {
  if (!meta || typeof meta !== "object") return false;
  const portal = meta.bk_checkpoints?.rectification;
  if (portal && typeof portal === "object") {
    if (portal.exists === true) return true;
    if (portal.exists === false) return false;
  }
  const r = meta.checkpoints?.rectification;
  if (r == null) return false;
  if (typeof r !== "object") return false;
  if (r.exists === true) return true;
  if (r.exists === false) return false;
  return !!(r.file_key || r.file_name || r.mesh_key || r.version != null);
}

export function getLockingBulldogEntry(meta) {
  const locking = unwrapLocking(meta);
  if (!locking) return null;
  if ('plain' in locking || 'bulldog' in locking) return locking.bulldog ?? null;
  return null; // legacy flat shape has no bulldog
}

/** Prefer bulldog if present, else plain. Used for "show final manufacturing mesh" flows. */
export function getPreferredLockingEntry(meta) {
  return getLockingBulldogEntry(meta) || getLockingPlainEntry(meta);
}

/** True if any locking variant exists on the server. */
export function hasAnyLockingCheckpoint(meta) {
  return getCheckpointMeshUrl(getLockingBulldogEntry(meta)) !== '' ||
    getCheckpointMeshUrl(getLockingPlainEntry(meta)) !== '';
}

/**
 * Read one server checkpoint slot, tolerating the two shapes the backend uses:
 * a plain entry (`checkpoints.rectification`) or a variant map
 * (`checkpoints.locking.{plain,bulldog}`).
 */
function readSlotEntry(meta, slot, variant) {
  const raw = meta?.checkpoints?.[slot];
  if (!raw || typeof raw !== 'object') return null;
  const isVariantMap = 'plain' in raw || 'bulldog' in raw;
  if (!isVariantMap) return raw;
  if (variant) return raw[variant] ?? null;
  return raw.bulldog ?? raw.plain ?? null;
}

/** Metadata blob stored alongside a checkpoint entry, whatever key it landed under. */
export function getCheckpointEntryMetadata(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const raw = entry.metadata ?? entry.meta ?? null;
  if (raw == null) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return typeof raw === 'object' ? raw : null;
}

/**
 * Server checkpoint entry for a workflow step.
 *
 * Prefers the step's own slot. Falls back to the pre-per-step-slot location
 * (see `LEGACY_SLOT_FALLBACK`) only when that entry is actually tagged as having
 * been produced by this step — otherwise a rectification checkpoint would read
 * as a valve checkpoint, since the two shared one slot.
 *
 * @returns {object|null}
 */
export function getStepCheckpointEntry(meta, stepId) {
  const slot = checkpointSlotForStep(stepId);
  if (!slot || !meta) return null;

  const own = readSlotEntry(meta, slot);
  if (own && getCheckpointMeshUrl(own) !== '') return own;

  const fallback = LEGACY_SLOT_FALLBACK[stepId];
  if (!fallback) return null;

  const legacy = readSlotEntry(meta, fallback.slot, fallback.variant);
  if (!legacy || getCheckpointMeshUrl(legacy) === '') return null;
  if (!fallback.tagStep) return legacy;

  const tag =
    getCheckpointEntryMetadata(legacy)?.checkpoint_step ?? legacy.checkpoint_step ?? null;
  return tag === fallback.tagStep ? legacy : null;
}

/** True when the server holds a mesh checkpoint for this step. */
export function hasStepCheckpointOnServer(meta, stepId) {
  return getStepCheckpointEntry(meta, stepId) != null;
}

/** Saved tool/session metadata for a step, or null. */
export function getStepCheckpointMetadata(meta, stepId) {
  return getCheckpointEntryMetadata(getStepCheckpointEntry(meta, stepId));
}

/** Checkpoint-bearing steps present on the server, in workflow order. */
export function listCheckpointStepsOnServer(meta) {
  if (!meta) return [];
  return CHECKPOINT_STEPS.filter((step) => hasStepCheckpointOnServer(meta, step));
}

/** Last step (in workflow order) that has a server checkpoint, or null. */
export function getLastCheckpointStepOnServer(meta) {
  const present = listCheckpointStepsOnServer(meta);
  return present.length > 0 ? present[present.length - 1] : null;
}

/**
 * Return the highest-version entry from `meta.final_files` (preferred — it
 * carries the public `file_url`) or `meta.final_versions` (fallback). The
 * entry shape is compatible with `getCheckpointMeshUrl`.
 *
 * IMPORTANT: `final_versions` entries only expose presigned URLs on the
 * private R2 endpoint (`*.r2.cloudflarestorage.com`), which `pickSafeGetUrl`
 * skips because they may be PUT-only. We therefore look at `final_files`
 * first, because it includes `file_url` on the public `pub-*.r2.dev` host.
 *
 * @returns {object|null}
 */
export function getLatestFinalFileEntry(meta) {
  const versionFromName = (name) => {
    const m = String(name || '').match(/_v(\d+)\.[a-zA-Z0-9]+$/);
    return m ? Number(m[1]) : -Infinity;
  };

  const files = Array.isArray(meta?.final_files) ? meta.final_files : [];
  if (files.length > 0) {
    let best = null;
    let bestV = -Infinity;
    for (const f of files) {
      if (getCheckpointMeshUrl(f) === '') continue;
      const v = versionFromName(f?.file_name);
      if (v > bestV) { best = f; bestV = v; }
    }
    if (best) return best;
    // No usable public URL on any final_files entry — fall through to versions.
  }

  const versions = Array.isArray(meta?.final_versions) ? meta.final_versions : [];
  if (versions.length > 0) {
    let best = null;
    for (const v of versions) {
      if (getCheckpointMeshUrl(v) === '') continue;
      const n = Number(v?.version);
      if (!Number.isFinite(n)) continue;
      if (!best || n > Number(best.version)) best = v;
    }
    if (best) return best;
  }

  // Final fallback: return the first entry that exists at all, so callers
  // can still surface "exists but unreachable" instead of silently rendering
  // nothing.
  if (files.length > 0) return files[files.length - 1];
  if (versions.length > 0) return versions[versions.length - 1];
  return null;
}

/**
 * Resolve the best mesh to show on the Finalization step in priority order:
 *   1. Bulldog locking checkpoint  (final manufacturing mesh with attachment)
 *   2. Plain locking checkpoint    (final manufacturing mesh without attachment)
 *   3. Latest exported final file  (`final_files` / `final_versions`)
 *
 * Returns `{ entry, kind, url, type }` or `null` if no candidate is found.
 *
 * @param {Record<string, unknown> | null | undefined} meta
 * @returns {{ entry: object, kind: 'bulldog'|'plain'|'final_export', url: string, type: 'stl'|'ply' } | null}
 */
export function pickFinalizationMeshCandidate(meta) {
  const candidates = [
    { kind: 'bulldog', entry: getLockingBulldogEntry(meta) },
    { kind: 'plain', entry: getLockingPlainEntry(meta) },
    { kind: 'final_export', entry: getLatestFinalFileEntry(meta) },
  ];

  for (const c of candidates) {
    if (!c.entry) continue;
    const url = getCheckpointMeshUrl(c.entry);
    if (!url) continue;
    const declared = c.entry.mesh_format;
    const type =
      declared === 'ply' || declared === 'stl'
        ? declared
        : detectMeshFileType(c.entry.file_name || '') || 'stl';
    if (type !== 'stl' && type !== 'ply') continue;
    return { entry: c.entry, kind: c.kind, url, type };
  }
  return null;
}

/**
 * Read a fetch Response body into a Blob, reporting download progress (0..1)
 * when a Content-Length is available. Falls back to `resp.blob()` when the body
 * can't be streamed or the size is unknown.
 * @param {Response} resp
 * @param {(fraction: number) => void} [onProgress]
 * @param {AbortSignal} [signal]
 * @returns {Promise<Blob>}
 */
async function readResponseBlobWithProgress(resp, onProgress, signal) {
  const total = Number(resp.headers.get('content-length')) || 0;
  if (!onProgress || !total || !resp.body || typeof resp.body.getReader !== 'function') {
    return resp.blob();
  }
  const reader = resp.body.getReader();
  const chunks = [];
  let received = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    chunks.push(value);
    received += value.length;
    onProgress(Math.max(0, Math.min(1, received / total)));
  }
  return new Blob(chunks);
}

/**
 * Load a single checkpoint mesh from a presigned/public URL into a blob URL for the editor.
 * Supports STL and PLY (OBJ checkpoints are not supported in this helper).
 *
 * @param {string} url
 * @param {'stl'|'ply'} type
 * @param {AbortSignal} [signal]
 * @param {(fraction: number) => void} [onProgress] - download progress 0..1 (when size is known)
 * @returns {Promise<{ type: 'stl'|'ply', data: string }>}
 */
export async function loadCheckpointMeshFromUrl(url, type, signal, onProgress) {
  if (!url) throw new Error('Missing checkpoint URL');
  const guessContentType =
    type === 'stl' ? 'model/stl' : type === 'ply' ? 'model/ply' : 'application/octet-stream';
  const contentTypesToTry = [null, guessContentType, 'application/octet-stream'];

  let lastErr = null;
  for (let i = 0; i < contentTypesToTry.length; i++) {
    const contentType = contentTypesToTry[i];
    const headers = contentType ? { 'Content-Type': contentType } : undefined;
    const resp = await fetch(url, { signal, headers });
    if (resp.ok) {
      if (type !== 'stl' && type !== 'ply') {
        throw new Error(`Unsupported checkpoint mesh type: ${type}`);
      }
      const blob = await readResponseBlobWithProgress(resp, onProgress, signal);
      const blobUrl = URL.createObjectURL(blob);
      return { type, data: blobUrl };
    }

    const bodyText = await resp.text().catch(() => '');
    const isSigMismatch = bodyText.includes('SignatureDoesNotMatch');
    lastErr = new Error(`Failed to fetch checkpoint mesh: ${resp.status}`);
    if (!isSigMismatch || i === contentTypesToTry.length - 1) throw lastErr;
  }

  throw lastErr || new Error('Failed to fetch checkpoint mesh');
}

export function getSourceEntryName(entry) {
  if (!entry) return '';
  return entry.file_name || entry.name || entry.key || '';
}

export function detectMeshFileType(filename) {
  const ext = filename.slice(filename.lastIndexOf('.')).toLowerCase();
  if (ext === '.stl') return 'stl';
  if (ext === '.ply') return 'ply';
  if (ext === '.obj') return 'obj';
  return null;
}

/** True if order already has at least one mesh source on the server */
export function hasMeshSourceOnServer(sourceFiles) {
  if (!Array.isArray(sourceFiles)) return false;
  return sourceFiles.some((e) => {
    const name = getSourceEntryName(e);
    return detectMeshFileType(name) && getSourceEntryUrl(e);
  });
}

/**
 * Pick best mesh entry: STL > PLY > first OBJ (by array order among same rank).
 * @returns {{ entry: object, name: string, type: 'stl'|'ply'|'obj', url: string } | null}
 */
export function pickPrimaryMeshSourceEntry(sourceFiles) {
  if (!Array.isArray(sourceFiles) || sourceFiles.length === 0) return null;

  const meshes = [];
  for (const entry of sourceFiles) {
    const name = getSourceEntryName(entry);
    const type = detectMeshFileType(name);
    const url = getSourceEntryUrl(entry);
    if (type && url) meshes.push({ entry, name, type, url });
  }
  if (meshes.length === 0) return null;

  const rank = (t) => (t === 'stl' ? 0 : t === 'ply' ? 1 : 2);
  meshes.sort((a, b) => rank(a.type) - rank(b.type) || a.name.localeCompare(b.name));
  return meshes[0];
}

export function formatSourceFileSize(bytes) {
  if (bytes == null || Number.isNaN(Number(bytes))) return '';
  const n = Number(bytes);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

async function urlToFile(url, fileName, signal) {
  const lower = String(fileName || '').toLowerCase();
  const guessContentType = (() => {
    if (lower.endsWith('.stl')) return 'model/stl';
    if (lower.endsWith('.ply')) return 'model/ply';
    if (lower.endsWith('.obj')) return 'model/obj';
    if (lower.endsWith('.mtl')) return 'model/mtl';
    if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
    if (lower.endsWith('.png')) return 'image/png';
    if (lower.endsWith('.webp')) return 'image/webp';
    return 'application/octet-stream';
  })();

  const contentTypesToTry = [guessContentType, 'application/octet-stream'];
  contentTypesToTry.unshift(null);

  let lastErr = null;
  for (let i = 0; i < contentTypesToTry.length; i++) {
    const contentType = contentTypesToTry[i];
    const headers = contentType ? { 'Content-Type': contentType } : undefined;

    const resp = await fetch(url, { signal, headers });
    if (resp.ok) {
      const blob = await resp.blob();
      const safeName = String(fileName || '');
      return new File(
        [blob],
        safeName.split('/').pop() || safeName || 'download',
        { type: blob.type || 'application/octet-stream' }
      );
    }

    const bodyText = await resp.text().catch(() => '');
    const isSigMismatch = bodyText.includes('SignatureDoesNotMatch');
    lastErr = new Error(`Failed to fetch ${fileName}: ${resp.status}`);
    if (!isSigMismatch || i === contentTypesToTry.length - 1) throw lastErr;
  }

  throw lastErr || new Error(`Failed to fetch ${fileName}`);
}

/**
 * Load primary mesh + companions for OBJ (mtl + textures) from source_files list.
 * @returns {Promise<{ type: 'stl'|'ply'|'obj', data: string|object }>}
 */
export async function loadMeshFromSourceFiles(sourceFiles, signal) {
  const primary = pickPrimaryMeshSourceEntry(sourceFiles);
  if (!primary) throw new Error('No mesh file in source_files');

  const check = () => {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  };

  if (primary.type === 'stl' || primary.type === 'ply') {
    check();
    const file = await urlToFile(primary.url, primary.name, signal);
    check();
    const blobUrl = URL.createObjectURL(file);
    return { type: primary.type, data: blobUrl };
  }

  // OBJ: primary .obj + .mtl + images from same listing
  check();
  const objFile = await urlToFile(primary.url, primary.name, signal);
  check();

  const objStem = primary.name.replace(/^.*\//, '').replace(/\.obj$/i, '');
  const entries = sourceFiles.map((e) => ({
    name: getSourceEntryName(e),
    url: getSourceEntryUrl(e),
  }));

  const mtlCandidates = entries.filter(
    (x) => x.name.toLowerCase().endsWith('.mtl') && x.url
  );
  const mtlFile =
    mtlCandidates.find((x) => {
      const stem = x.name.replace(/^.*\//, '').replace(/\.mtl$/i, '');
      return objStem.includes(stem) || stem.includes(objStem) || stem === objStem;
    }) || mtlCandidates[0];

  let mtlAsFile = null;
  if (mtlFile) {
    mtlAsFile = await urlToFile(mtlFile.url, mtlFile.name, signal);
    check();
  }

  const textureFiles = [];
  for (const x of entries) {
    const lower = x.name.toLowerCase();
    if (!x.url) continue;
    if (!/\.(jpg|jpeg|png|webp)$/i.test(lower)) continue;
    check();
    textureFiles.push(await urlToFile(x.url, x.name, signal));
  }

  const { createOBJBlobUrls } = await import('./fileUploadUtils');
  const blobUrls = createOBJBlobUrls({
    objFile,
    mtlFile: mtlAsFile,
    textureFiles,
  });
  return { type: 'obj', data: blobUrls };
}
